/**
 * Cookie Policy Page
 */

import { Link } from 'react-router-dom'
import { Footer } from '@/components/layout/Footer'
import { SEO } from '@/components/seo/SEO'

export const Cookies = () => {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* SEO */}
      <SEO
        title="Cookie Policy"
        description="Learn about how Paiseh! uses cookies. We use minimal cookies to provide authentication and essential functionality for your wishlist experience."
        canonicalUrl="https://paiseh.sg/cookies"
        noIndex={false}
      />

      {/* Main Content */}
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Cookie Policy</h1>
        <p className="text-sm text-gray-600 mb-8">Last updated: December 26, 2024</p>

        <div className="prose prose-gray max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">What Are Cookies</h2>
            <p className="text-gray-700 mb-4">
              Cookies are small text files that are stored on your device when you visit a website. They help websites remember your preferences and improve your browsing experience.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">How We Use Cookies</h2>
            <p className="text-gray-700 mb-4">
            paiseh.sg uses cookies to provide essential functionality and improve your experience. We use the following types of cookies:
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Types of Cookies We Use</h2>

            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Essential Cookies (Required)</h3>
                <p className="text-gray-700 mb-3">
                  These cookies are necessary for the website to function properly. Without these cookies, we cannot provide the Service.
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li><strong>Authentication cookies:</strong> Keep you logged in to your account</li>
                  <li><strong>Session cookies:</strong> Maintain your session state as you navigate the site</li>
                  <li><strong>Security cookies:</strong> Protect against fraud and unauthorized access</li>
                </ul>
                <p className="text-sm text-gray-600 mt-3">
                  Duration: Session cookies (deleted when you close your browser) or up to 30 days
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Functional Cookies</h3>
                <p className="text-gray-700 mb-3">
                  These cookies enable enhanced functionality and personalization.
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li><strong>Preference cookies:</strong> Remember your settings and preferences</li>
                  <li><strong>Language cookies:</strong> Remember your language selection</li>
                  <li><strong>Theme cookies:</strong> Remember your theme preferences (if applicable)</li>
                </ul>
                <p className="text-sm text-gray-600 mt-3">
                  Duration: Up to 1 year
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Performance Cookies</h3>
                <p className="text-gray-700 mb-3">
                  These cookies help us understand how visitors interact with our website by collecting anonymous information.
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li><strong>Analytics cookies:</strong> Track page views and navigation patterns</li>
                  <li><strong>Error tracking:</strong> Help us identify and fix technical issues</li>
                </ul>
                <p className="text-sm text-gray-600 mt-3">
                  Duration: Up to 2 years
                </p>
                <p className="text-sm text-blue-600 mt-2">
                  Note: We do not currently use performance cookies, but may add them in the future with your consent.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Third-Party Cookies</h2>
            <p className="text-gray-700 mb-4">
              We use the following third-party services that may set cookies:
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-900 mb-2">Firebase Authentication (Google)</h4>
              <p className="text-sm text-gray-700 mb-2">
                Used for user authentication and session management.
              </p>
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                View Google Privacy Policy →
              </a>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Managing Cookies</h2>
            <p className="text-gray-700 mb-4">
              You can control and manage cookies in several ways:
            </p>

            <h3 className="text-xl font-medium text-gray-800 mb-3">Browser Settings</h3>
            <p className="text-gray-700 mb-4">
              Most browsers allow you to refuse or accept cookies. Here's how to manage cookies in common browsers:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
              <li>
                <strong>Chrome:</strong>{' '}
                <a
                  href="https://support.google.com/chrome/answer/95647"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700"
                >
                  Cookie settings
                </a>
              </li>
              <li>
                <strong>Firefox:</strong>{' '}
                <a
                  href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700"
                >
                  Cookie settings
                </a>
              </li>
              <li>
                <strong>Safari:</strong>{' '}
                <a
                  href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700"
                >
                  Cookie settings
                </a>
              </li>
              <li>
                <strong>Edge:</strong>{' '}
                <a
                  href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700"
                >
                  Cookie settings
                </a>
              </li>
            </ul>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-yellow-900 font-medium mb-2">⚠️ Important</p>
              <p className="text-sm text-yellow-800">
                Blocking essential cookies will prevent you from using paiseh.sg. You will not be able to log in or access your wishlists.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Do Not Track</h2>
            <p className="text-gray-700 mb-4">
              We respect Do Not Track (DNT) browser signals. However, note that essential cookies are still required for the Service to function.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Updates to This Policy</h2>
            <p className="text-gray-700 mb-4">
              We may update this Cookie Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">More Information</h2>
            <p className="text-gray-700 mb-4">
              For more information about how we handle your data, please read our{' '}
              <Link to="/privacy" className="text-blue-600 hover:text-blue-700">
                Privacy Policy
              </Link>
              .
            </p>
            <p className="text-gray-700">
              If you have questions about our use of cookies, contact us at{' '}
              <a href="mailto:liu.kwn@gmail.com?subject=[Paiseh.sg]%20Cookie%20Policy" className="text-blue-600 hover:text-blue-700">
                liu.kwn@gmail.com
              </a>
            </p>
          </section>
        </div>

        <div className="mt-8 pt-8 border-t border-gray-200">
          <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
            ← Back to Home
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  )
}
